import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, User, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useSite } from "../../contexts/SiteContext";

function Logo({ brand }) {
    if (brand.logo_url) {
        return (
            <span className="flex items-center gap-2">
                <img src={brand.logo_url} alt={brand.name} className="h-9 max-h-9 w-auto object-contain" />
            </span>
        );
    }
    return (
        <span className="flex items-center gap-2">
            <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold sm:h-9 sm:w-9">
                <span className="font-display text-sm text-gold sm:text-base">{brand.logo_initial || "L"}</span>
                <span className="absolute -right-1 -top-1 text-heart">♥</span>
            </span>
            <span className="font-display text-lg tracking-[0.15em] text-gold sm:text-2xl">{(brand.name || "LovinLife").toUpperCase()}</span>
        </span>
    );
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const { count, setIsOpen } = useCart();
    const { settings } = useSite();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();

    const links = settings.nav_links || [];
    const brand = settings.brand || {};

    const handleLogout = async () => {
        await logout();
        setMenuOpen(false);
        navigate("/");
    };

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-40 border-b border-border-dim bg-obsidian/90 backdrop-blur-xl"
        >
            <div className="mx-auto flex h-16 max-w-[1480px] items-center justify-between px-4 sm:px-6 md:h-20 lg:px-12">
                <Link to="/" data-testid="logo-link" className="group">
                    <Logo brand={brand} />
                </Link>

                <nav className="hidden items-center gap-10 md:flex">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[13px] uppercase tracking-[0.2em] transition-colors ${
                                    isActive ? "text-gold" : "text-white/80 hover:text-gold"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-1 sm:gap-2">
                    <button
                        data-testid="open-wishlist"
                        onClick={() => navigate(user ? "/account?tab=wishlist" : "/login")}
                        className="hidden h-10 w-10 items-center justify-center text-white/80 transition-colors hover:text-heart md:inline-flex"
                        aria-label="Wishlist"
                    >
                        <Heart className="h-5 w-5" strokeWidth={1.4} />
                    </button>
                    <div className="relative hidden md:inline-block">
                        <button
                            data-testid="user-menu-trigger"
                            onClick={() => setMenuOpen((o) => !o)}
                            className="flex h-10 w-10 items-center justify-center text-white/80 transition-colors hover:text-gold"
                            aria-label="Account"
                        >
                            <User className="h-5 w-5" strokeWidth={1.4} />
                        </button>
                        {menuOpen && (
                            <div
                                data-testid="user-menu"
                                className="absolute right-0 top-12 w-56 border border-border-dim bg-surface p-2 shadow-2xl"
                            >
                                {user ? (
                                    <>
                                        <div className="px-3 py-2">
                                            <div className="text-xs uppercase tracking-widest text-white/40">Signed in</div>
                                            <div className="truncate text-sm text-white">{user.name}</div>
                                        </div>
                                        <Link
                                            to="/account"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="menu-account"
                                            className="block px-3 py-2 text-sm text-white/80 hover:bg-surface-2 hover:text-gold"
                                        >
                                            My account
                                        </Link>
                                        {user.role === "admin" && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setMenuOpen(false)}
                                                data-testid="menu-admin"
                                                className="flex items-center gap-2 px-3 py-2 text-sm text-gold hover:bg-surface-2"
                                            >
                                                <LayoutDashboard className="h-4 w-4" /> Admin dashboard
                                            </Link>
                                        )}
                                        <button
                                            onClick={handleLogout}
                                            data-testid="menu-logout"
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-surface-2 hover:text-heart"
                                        >
                                            <LogOut className="h-4 w-4" /> Sign out
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/login"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="menu-login"
                                            className="block px-3 py-2 text-sm text-white/80 hover:bg-surface-2 hover:text-gold"
                                        >
                                            Sign in
                                        </Link>
                                        <Link
                                            to="/register"
                                            onClick={() => setMenuOpen(false)}
                                            data-testid="menu-register"
                                            className="block px-3 py-2 text-sm text-gold hover:bg-surface-2"
                                        >
                                            Create account
                                        </Link>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        data-testid="cart-trigger"
                        onClick={() => setIsOpen(true)}
                        className="relative flex h-10 items-center gap-2 px-3 text-white/90 transition-colors hover:text-gold"
                    >
                        <ShoppingBag className="h-5 w-5" strokeWidth={1.4} />
                        <span className="hidden text-[13px] uppercase tracking-widest md:inline">Bag</span>
                        {count > 0 && (
                            <span
                                data-testid="cart-count"
                                className="absolute -right-1 top-1 flex h-5 w-5 items-center justify-center bg-gold text-[10px] font-semibold text-obsidian"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="mobile-menu-trigger"
                        onClick={() => setMobileOpen(true)}
                        className="inline-flex h-10 w-10 items-center justify-center text-white md:hidden"
                        aria-label="Menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="fixed inset-0 z-50 bg-obsidian md:hidden" data-testid="mobile-menu">
                    <div className="flex h-16 items-center justify-between px-4">
                        <Logo brand={brand} />
                        <button
                            onClick={() => setMobileOpen(false)}
                            data-testid="mobile-menu-close"
                            className="h-10 w-10 text-white"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <nav className="flex flex-col gap-2 px-6">
                        {links.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setMobileOpen(false)}
                                className="border-b border-border-dim py-4 font-display text-3xl text-white hover:text-gold"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to={user ? "/account" : "/login"}
                            onClick={() => setMobileOpen(false)}
                            className="border-b border-border-dim py-4 font-display text-3xl text-white hover:text-gold"
                        >
                            {user ? "Account" : "Sign In"}
                        </Link>
                        {user?.role === "admin" && (
                            <Link
                                to="/admin"
                                onClick={() => setMobileOpen(false)}
                                className="py-4 font-display text-3xl text-gold"
                            >
                                Admin
                            </Link>
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}
